import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import {
  Shield,
  Sparkles,
  Wrench,
  History,
  QrCode,
  AlertTriangle,
  CheckCircle,
  X,
  Edit2,
  Trash2,
  Printer,
  Download,
  Copy,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Cleaning status helper - returns color based on last cleaned date
const getCleaningColor = (lastCleaned) => {
  if (!lastCleaned) return '#ef4444'; // red - never cleaned
  const daysSince = (Date.now() - new Date(lastCleaned).getTime()) / (1000 * 60 * 60 * 24);
  if (daysSince <= 14) return '#22c55e'; // green - recent
  if (daysSince <= 30) return '#eab308'; // yellow - getting old
  if (daysSince <= 60) return '#f97316'; // orange - old
  return '#ef4444'; // red - very old
};

// Build a short, human-friendly unique code per weapon card (deterministic from card id)
const buildWeaponCode = (cardId) => {
  if (!cardId) return 'AKT-MV---------';
  const cleaned = String(cardId).replace(/-/g, '').toUpperCase();
  return `AKT-MV-${cleaned.slice(0, 8)}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatDate = (iso) => {
  if (!iso) return 'Aldri';
  try {
    return new Date(iso).toLocaleDateString('nb-NO');
  } catch {
    return iso;
  }
};

const MyWeaponMaintenance = () => {
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState('list'); // list | history
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState({ most_cleaned: [], least_cleaned: [] });
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 10;
  const [loading, setLoading] = useState(true);

  // Modal states
  const [cleaningCard, setCleaningCard] = useState(null);
  const [cleaningNotes, setCleaningNotes] = useState('');
  const [maintenanceCard, setMaintenanceCard] = useState(null);
  const [maintenanceDescription, setMaintenanceDescription] = useState('');
  const [maintenanceCost, setMaintenanceCost] = useState('');
  const [qrActionCard, setQrActionCard] = useState(null);

  // Edit/Delete history
  const [editingItem, setEditingItem] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCost, setEditCost] = useState('');

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [cardsRes, statsRes, historyRes] = await Promise.all([
        axios.get(`${API_BASE}/api/my-weapon-cards`, { headers }),
        axios.get(`${API_BASE}/api/my-weapons/stats`, { headers }),
        axios.get(`${API_BASE}/api/my-weapons/history`, { headers }),
      ]);
      setCards(cardsRes.data.cards || []);
      setStats(statsRes.data || { most_cleaned: [], least_cleaned: [] });
      setHistory(historyRes.data.history || []);
    } catch (e) {
      console.error('Error loading my-weapon maintenance:', e);
      addToast('Kunne ikke laste våpenpuss-data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Filter history client-side
  const filteredHistory = useMemo(() => {
    if (historyFilter === 'all') return history;
    return history.filter((h) => h.type === historyFilter);
  }, [history, historyFilter]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE));
  const safePage = Math.min(historyPage, totalPages);
  const pagedHistory = useMemo(() => {
    const start = (safePage - 1) * HISTORY_PAGE_SIZE;
    return filteredHistory.slice(start, start + HISTORY_PAGE_SIZE);
  }, [filteredHistory, safePage]);

  // Reset to page 1 whenever the filter or underlying data changes
  useEffect(() => {
    setHistoryPage(1);
  }, [historyFilter, history.length]);

  const handleLogCleaning = async () => {
    if (!cleaningCard) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/my-weapon-cards/${cleaningCard.id}/clean`,
        { notes: cleaningNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      addToast('Pussing registrert', 'success');
      setCleaningCard(null);
      setCleaningNotes('');
      await fetchAll();
    } catch (e) {
      console.error(e);
      addToast('Kunne ikke registrere pussing', 'error');
    }
  };

  const handleLogMaintenance = async () => {
    if (!maintenanceCard) return;
    if (!maintenanceDescription.trim()) {
      addToast('Beskrivelse er påkrevd', 'error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = { description: maintenanceDescription };
      if (maintenanceCost) payload.cost = parseFloat(maintenanceCost);
      await axios.post(
        `${API_BASE}/api/my-weapon-cards/${maintenanceCard.id}/maintenance`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      addToast('Vedlikehold registrert – se i Historikk', 'success');
      setMaintenanceCard(null);
      setMaintenanceDescription('');
      setMaintenanceCost('');
      await fetchAll();
    } catch (e) {
      console.error(e);
      addToast('Kunne ikke registrere vedlikehold', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    try {
      const token = localStorage.getItem('token');
      const endpoint =
        editingItem.type === 'cleaning'
          ? `/api/my-weapon-cards/cleaning/${editingItem.id}`
          : `/api/my-weapon-cards/maintenance/${editingItem.id}`;
      const payload =
        editingItem.type === 'cleaning'
          ? { notes: editNotes }
          : { description: editDescription, cost: editCost ? parseFloat(editCost) : null };
      await axios.put(`${API_BASE}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      addToast('Oppdatert', 'success');
      setEditingItem(null);
      await fetchAll();
    } catch (e) {
      console.error(e);
      addToast('Kunne ikke oppdatere', 'error');
    }
  };

  const handleDeleteHistoryItem = async (item) => {
    if (
      !window.confirm(
        `Er du sikker på at du vil slette denne ${
          item.type === 'cleaning' ? 'pussing' : 'vedlikehold'
        }s-hendelsen?`
      )
    ) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const endpoint =
        item.type === 'cleaning'
          ? `/api/my-weapon-cards/cleaning/${item.id}`
          : `/api/my-weapon-cards/maintenance/${item.id}`;
      await axios.delete(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      addToast('Slettet', 'success');
      await fetchAll();
    } catch (e) {
      console.error(e);
      addToast('Kunne ikke slette', 'error');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    if (item.type === 'cleaning') {
      setEditNotes(item.notes || '');
    } else {
      setEditDescription(item.description || '');
      setEditCost(item.cost != null ? String(item.cost) : '');
    }
  };

  // Generate a PNG data URL containing the AKT-MV QR code + weapon info.
  const buildQrPng = async (card) => {
    const code = buildWeaponCode(card.id);
    const qrDataUrl = await QRCode.toDataURL(code, {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const qrSize = 300;
    const textHeight = 180;
    const sidePadding = 60;

    ctx.font = 'bold 36px Arial, sans-serif';
    const brandModelText = `${card.weapon_brand || ''} ${card.weapon_model || ''}`.trim();
    const brandModelWidth = ctx.measureText(brandModelText).width;
    ctx.font = '28px Arial, sans-serif';
    const caliberText = `Kaliber: ${card.caliber || ''}`;
    const caliberWidth = ctx.measureText(caliberText).width;
    ctx.font = 'bold 26px Arial, sans-serif';
    const codeText = code;
    const codeWidth = ctx.measureText(codeText).width;

    const maxTextWidth = Math.max(brandModelWidth, caliberWidth, codeWidth);
    canvas.width = Math.max(qrSize + sidePadding * 2, maxTextWidth + sidePadding * 2);
    canvas.height = qrSize + textHeight + 40;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const qrImage = new Image();
    await new Promise((resolve, reject) => {
      qrImage.onload = resolve;
      qrImage.onerror = reject;
      qrImage.src = qrDataUrl;
    });

    const qrX = (canvas.width - qrSize) / 2;
    ctx.drawImage(qrImage, qrX, 20, qrSize, qrSize);

    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.font = 'bold 36px Arial, sans-serif';
    ctx.fillText(brandModelText || 'Eget våpen', canvas.width / 2, qrSize + 65);
    ctx.font = '28px Arial, sans-serif';
    ctx.fillText(caliberText, canvas.width / 2, qrSize + 110);
    ctx.font = 'bold 26px Arial, sans-serif';
    ctx.fillStyle = '#9a7d00';
    ctx.fillText(codeText, canvas.width / 2, qrSize + 155);

    return { dataUrl: canvas.toDataURL('image/png'), code, brandModelText };
  };

  const handlePrintQR = async (card) => {
    try {
      const { dataUrl, code } = await buildQrPng(card);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>QR-kode ${code}</title>
              <style>
                body { margin: 0; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #fff; }
                img { max-width: 90%; height: auto; }
                @media print { body { min-height: auto; } }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" alt="QR ${code}" onload="window.focus(); window.print();" />
            </body>
          </html>
        `);
        printWindow.document.close();
        addToast('Utskriftsvindu åpnet', 'success');
      } else {
        addToast('Popup blokkert – tillat popup eller bruk Last ned', 'warning');
      }
    } catch (e) {
      console.error('Error printing QR:', e);
      addToast('Kunne ikke skrive ut QR-kode', 'error');
    }
  };

  const handleDownloadQR = async (card) => {
    try {
      const { dataUrl, code } = await buildQrPng(card);
      const link = document.createElement('a');
      link.download = `QR-${(card.weapon_brand || 'vapen')}-${(card.weapon_model || '')}-${code}.png`.replace(/\s+/g, '_');
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      addToast('QR-kode lastet ned', 'success');
    } catch (e) {
      console.error('Error downloading QR:', e);
      addToast('Kunne ikke laste ned QR-kode', 'error');
    }
  };

  const handleCopyQR = async (card) => {
    try {
      const { dataUrl } = await buildQrPng(card);
      // Convert data URL to Blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        addToast('QR-kode kopiert til utklippstavlen', 'success');
      } else {
        // Fallback - download
        await handleDownloadQR(card);
        addToast('Nettleseren støtter ikke kopier-bilde, lastet ned i stedet', 'info');
      }
    } catch (e) {
      console.error('Error copying QR:', e);
      addToast('Kunne ikke kopiere QR-kode', 'error');
    }
  };

  if (loading) {
    return (
      <div
        className="bg-gray-800 rounded-lg p-6"
        data-testid="my-weapon-maintenance-loading"
      >
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="h-6 w-6 text-yellow-400" />
          <h2 className="text-xl font-bold text-white">Våpenpuss egne våpen</h2>
        </div>
        <p className="text-gray-400">Laster...</p>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-800 rounded-lg p-4 sm:p-6"
      data-testid="my-weapon-maintenance"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6 text-yellow-400" />
          <div>
            <h2 className="text-xl font-bold text-white">Våpenpuss egne våpen</h2>
            <p className="text-sm text-gray-400">
              Registrer pussing og vedlikehold på dine egne våpen. Kun du har tilgang til dette.
            </p>
          </div>
        </div>
        <Link
          to="/weapon-scanner"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-gray-900 font-semibold rounded-lg transition-all shadow-md hover:shadow-lg text-sm"
          data-testid="mwm-scan-qr-button"
        >
          <QrCode className="w-5 h-5" />
          <span>Skann QR for å logge puss/vedlikehold av egne våpen</span>
        </Link>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 bg-gray-900/50 p-1 rounded-lg mb-5 w-full sm:w-fit"
        data-testid="mwm-tabs"
      >
        <button
          onClick={() => setActiveTab('list')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            activeTab === 'list'
              ? 'bg-yellow-500 text-gray-900'
              : 'text-gray-400 hover:text-white'
          }`}
          data-testid="mwm-tab-list"
        >
          <Shield className="w-4 h-4" />
          Mine våpen ({cards.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            activeTab === 'history'
              ? 'bg-yellow-500 text-gray-900'
              : 'text-gray-400 hover:text-white'
          }`}
          data-testid="mwm-tab-history"
        >
          <History className="w-4 h-4" />
          Historikk ({history.length})
        </button>
      </div>

      {/* LIST TAB */}
      {activeTab === 'list' && (
        <>
          {cards.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-gray-700 rounded-lg">
              <Shield className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 mb-1">Ingen våpen registrert ennå</p>
              <p className="text-sm text-gray-500">
                Legg til våpen i "Mine Våpenkort" over for å kunne registrere pussing.
              </p>
            </div>
          ) : (
            <div
              className="overflow-x-auto rounded-lg border border-gray-700"
              data-testid="my-weapons-table-wrap"
            >
              <table className="w-full table-fixed sm:table-auto" data-testid="my-weapons-table">
                <thead className="bg-gray-900/60">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-400">
                      Merke / Modell
                    </th>
                    <th className="px-1 sm:px-3 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-400">
                      Handlinger
                    </th>
                    <th className="px-1 sm:px-3 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-400">
                      Sist pusset / Vedlikeholdt
                    </th>
                    <th className="px-1 sm:px-3 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-400">
                      QR
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {cards.map((card) => {
                    const cleaningColor = getCleaningColor(card.last_cleaned);
                    return (
                      <tr
                        key={card.id}
                        className="bg-gray-800/40 hover:bg-gray-700/40 transition-colors"
                        data-testid={`my-weapon-row-${card.id}`}
                      >
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="min-w-0">
                            <p className="text-white font-medium leading-tight text-xs sm:text-base">
                              {card.weapon_brand}
                            </p>
                            <p className="text-gray-400 text-[11px] sm:text-sm leading-tight">
                              {card.weapon_model}
                              {card.caliber ? `, ${card.caliber}` : ''}
                            </p>
                          </div>
                        </td>
                        <td className="px-1 sm:px-3 py-2 sm:py-3">
                          <div className="flex flex-col items-stretch gap-1 min-w-0 sm:min-w-[100px]">
                            <button
                              onClick={() => {
                                setCleaningCard(card);
                                setCleaningNotes('');
                              }}
                              className="px-2 py-1 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded text-xs font-medium transition-colors"
                              title="Registrer pussing"
                              data-testid={`mwm-clean-${card.id}`}
                            >
                              Pussing
                            </button>
                            <button
                              onClick={() => {
                                setMaintenanceCard(card);
                                setMaintenanceDescription('');
                                setMaintenanceCost('');
                              }}
                              className="px-2 py-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded text-xs font-medium transition-colors"
                              title="Registrer vedlikehold"
                              data-testid={`mwm-maintenance-${card.id}`}
                            >
                              Vedlikehold
                            </button>
                          </div>
                        </td>
                        <td className="px-1 sm:px-3 py-2 sm:py-3 text-gray-300 text-xs sm:text-sm">
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-1 sm:gap-2 flex-wrap" title="Sist pusset">
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: cleaningColor }}
                                data-testid={`mwm-cleaning-dot-${card.id}`}
                              />
                              <Sparkles className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                              <span
                                className="text-yellow-300 font-medium text-xs"
                                title="Antall pussinger"
                                data-testid={`mwm-cleaning-count-${card.id}`}
                              >
                                {card.cleaning_count || 0}x
                              </span>
                              <span className="whitespace-nowrap">{formatDate(card.last_cleaned)}</span>
                            </span>
                            {card.last_maintenance && (
                              <span
                                className="flex items-center gap-1 sm:gap-2 flex-wrap text-[11px] sm:text-xs text-gray-400"
                                title="Sist vedlikeholdt"
                                data-testid={`mwm-last-maintenance-${card.id}`}
                              >
                                <span className="inline-block w-2.5 h-2.5 flex-shrink-0" />
                                <Wrench className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                {(card.maintenance_count || 0) > 0 && (
                                  <span
                                    className="text-blue-300 font-medium text-xs"
                                    title="Antall vedlikehold"
                                    data-testid={`mwm-maintenance-count-${card.id}`}
                                  >
                                    {card.maintenance_count}x
                                  </span>
                                )}
                                <span className="whitespace-nowrap">{formatDate(card.last_maintenance)}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-1 sm:px-3 py-2 sm:py-3 text-center">
                          <button
                            onClick={() => setQrActionCard(card)}
                            className="inline-flex items-center justify-center p-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded-lg transition-colors border border-yellow-500/30"
                            title="Klikk for å Skrive ut / Laste ned / vise QR-kode for dette våpenet"
                            aria-label="Klikk for å Skrive ut / Laste ned / vise QR-kode for dette våpenet"
                            data-testid={`mwm-print-qr-${card.id}`}
                          >
                            <QrCode className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Mest pusset / Minst pusset section */}
          {cards.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {/* Mest pusset */}
              <div
                className="bg-gray-900/40 border border-gray-700 rounded-lg p-5"
                data-testid="mwm-most-cleaned"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-white font-semibold">Mest pusset</h3>
                </div>
                {stats.most_cleaned && stats.most_cleaned.length > 0 ? (
                  <div className="space-y-3">
                    {stats.most_cleaned.map((item) => (
                      <div
                        key={`most-${item.id}`}
                        className="pb-3 border-b border-gray-700 last:border-0 last:pb-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">
                            {item.name || 'Ukjent våpen'}
                          </span>
                          <span className="text-green-400 font-medium">
                            {item.cleaning_count}x
                          </span>
                        </div>
                        {item.last_cleaned_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Sist: {item.last_cleaned_by || 'Deg'} -{' '}
                            {formatDateTime(item.last_cleaned_at)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Ingen pussing registrert</p>
                )}
              </div>

              {/* Minst pusset */}
              <div
                className="bg-gray-900/40 border border-gray-700 rounded-lg p-5"
                data-testid="mwm-least-cleaned"
              >
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  <h3 className="text-white font-semibold">Minst pusset</h3>
                </div>
                {stats.least_cleaned && stats.least_cleaned.length > 0 ? (
                  <div className="space-y-3">
                    {stats.least_cleaned.map((item) => (
                      <div
                        key={`least-${item.id}`}
                        className="pb-3 border-b border-gray-700 last:border-0 last:pb-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">
                            {item.name || 'Ukjent våpen'}
                          </span>
                          <span className="text-orange-400 font-medium">
                            {item.cleaning_count}x
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Ingen våpen å vise</p>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div data-testid="mwm-history-tab">
          {/* Filter */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm text-gray-400">Filter:</span>
            {[
              { value: 'all', label: 'Alle' },
              { value: 'cleaning', label: 'Pussing' },
              { value: 'maintenance', label: 'Vedlikehold' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setHistoryFilter(opt.value)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  historyFilter === opt.value
                    ? 'bg-yellow-500 text-gray-900 font-medium'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                data-testid={`mwm-history-filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {filteredHistory.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-gray-700 rounded-lg">
              <History className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Ingen historikk ennå</p>
              <p className="text-sm text-gray-500 mt-1">
                Registrer pussing eller vedlikehold på et våpen for å se det her.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {pagedHistory.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 bg-gray-900/40 border border-gray-700 rounded-lg"
                  data-testid={`mwm-history-item-${item.id}`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.type === 'cleaning'
                        ? 'bg-yellow-500/20 text-yellow-500'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}
                  >
                    {item.type === 'cleaning' ? (
                      <Sparkles className="w-5 h-5" />
                    ) : (
                      <Wrench className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.type === 'cleaning'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}
                      >
                        {item.type === 'cleaning' ? 'Pussing' : 'Vedlikehold'}
                      </span>
                      <span className="text-white font-medium">{item.weapon_name}</span>
                      {item.caliber && (
                        <span className="text-xs text-gray-500">({item.caliber})</span>
                      )}
                    </div>
                    {item.type === 'cleaning' && item.notes && (
                      <p className="text-sm text-gray-300 mt-1">{item.notes}</p>
                    )}
                    {item.type === 'maintenance' && (
                      <>
                        <p className="text-sm text-gray-300 mt-1">{item.description}</p>
                        {item.cost != null && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Kostnad: kr {item.cost}
                          </p>
                        )}
                      </>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDateTime(item.timestamp)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEditModal(item)}
                      className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Rediger"
                      data-testid={`mwm-edit-history-${item.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteHistoryItem(item)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Slett"
                      data-testid={`mwm-delete-history-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              </div>

              {/* Pagination controls */}
              {filteredHistory.length > HISTORY_PAGE_SIZE && (
                <div
                  className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-700"
                  data-testid="mwm-history-pagination"
                >
                  <span className="text-xs text-gray-400">
                    Viser {(safePage - 1) * HISTORY_PAGE_SIZE + 1}–
                    {Math.min(safePage * HISTORY_PAGE_SIZE, filteredHistory.length)} av{' '}
                    {filteredHistory.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      className="p-1.5 rounded-md bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      data-testid="mwm-history-prev"
                      aria-label="Forrige side"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-300 min-w-[3.5rem] text-center">
                      {safePage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      className="p-1.5 rounded-md bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      data-testid="mwm-history-next"
                      aria-label="Neste side"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CLEANING MODAL */}
      {cleaningCard && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setCleaningCard(null)}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="mwm-cleaning-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Registrer pussing
              </h3>
              <button
                onClick={() => setCleaningCard(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-1">
              {cleaningCard.weapon_brand} {cleaningCard.weapon_model}
            </p>
            <p className="text-xs font-mono text-yellow-400 mb-4">
              {buildWeaponCode(cleaningCard.id)}
            </p>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Notater (valgfritt)
            </label>
            <textarea
              value={cleaningNotes}
              onChange={(e) => setCleaningNotes(e.target.value)}
              rows={3}
              placeholder="Legg til notater om pussingen..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              data-testid="mwm-cleaning-notes"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setCleaningCard(null)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleLogCleaning}
                className="px-4 py-2 bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-2"
                data-testid="mwm-confirm-cleaning"
              >
                <CheckCircle className="w-4 h-4" />
                Registrer pussing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAINTENANCE MODAL */}
      {maintenanceCard && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setMaintenanceCard(null)}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="mwm-maintenance-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Wrench className="w-5 h-5 text-blue-400" />
                Registrer vedlikehold
              </h3>
              <button
                onClick={() => setMaintenanceCard(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-1">
              {maintenanceCard.weapon_brand} {maintenanceCard.weapon_model}
            </p>
            <p className="text-xs font-mono text-yellow-400 mb-4">
              {buildWeaponCode(maintenanceCard.id)}
            </p>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Beskrivelse <span className="text-red-400">*</span>
            </label>
            <textarea
              value={maintenanceDescription}
              onChange={(e) => setMaintenanceDescription(e.target.value)}
              rows={3}
              placeholder="Beskriv vedlikeholdet..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 mb-3"
              data-testid="mwm-maintenance-description"
            />
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Kostnad (NOK, valgfritt)
            </label>
            <input
              type="number"
              value={maintenanceCost}
              onChange={(e) => setMaintenanceCost(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              data-testid="mwm-maintenance-cost"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setMaintenanceCard(null)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleLogMaintenance}
                className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                data-testid="mwm-confirm-maintenance"
              >
                <CheckCircle className="w-4 h-4" />
                Registrer vedlikehold
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR ACTION MODAL (Skriv ut / Last ned / Kopier) */}
      {qrActionCard && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setQrActionCard(null)}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="mwm-qr-action-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <QrCode className="w-5 h-5 text-yellow-400" />
                QR-kode
              </h3>
              <button
                onClick={() => setQrActionCard(null)}
                className="text-gray-400 hover:text-white"
                data-testid="mwm-qr-action-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-1">
              {qrActionCard.weapon_brand} {qrActionCard.weapon_model}
              {qrActionCard.caliber ? `, ${qrActionCard.caliber}` : ''}
            </p>
            <p className="text-xs font-mono text-yellow-400 mb-5">
              {buildWeaponCode(qrActionCard.id)}
            </p>
            <div className="space-y-2">
              <button
                onClick={async () => {
                  await handlePrintQR(qrActionCard);
                  setQrActionCard(null);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors"
                data-testid="mwm-qr-action-print"
              >
                <Printer className="w-4 h-4" />
                Skriv ut
              </button>
              <button
                onClick={async () => {
                  await handleDownloadQR(qrActionCard);
                  setQrActionCard(null);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                data-testid="mwm-qr-action-download"
              >
                <Download className="w-4 h-4" />
                Last ned (PNG)
              </button>
              <button
                onClick={async () => {
                  await handleCopyQR(qrActionCard);
                  setQrActionCard(null);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                data-testid="mwm-qr-action-copy"
              >
                <Copy className="w-4 h-4" />
                Kopier til utklippstavle
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">
              Send QR-koden til deg selv via mobil, eller skriv den ut og fest på våpenkofferten.
            </p>
          </div>
        </div>
      )}

      {/* EDIT HISTORY MODAL */}
      {editingItem && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setEditingItem(null)}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="mwm-edit-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                Rediger {editingItem.type === 'cleaning' ? 'pussing' : 'vedlikehold'}
              </h3>
              <button
                onClick={() => setEditingItem(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {editingItem.type === 'cleaning' ? (
              <>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Notater
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                />
              </>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Beskrivelse
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 mb-3"
                />
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Kostnad (NOK)
                </label>
                <input
                  type="number"
                  value={editCost}
                  onChange={(e) => setEditCost(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditingItem(null)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-600 transition-colors"
                data-testid="mwm-confirm-edit"
              >
                Lagre
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWeaponMaintenance;
